import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Clock, Users, Award, Eye, Activity, Heart, Brain, Stethoscope, Microscope } from 'lucide-react';
import { Link } from 'react-router-dom';
import SlideshowBackground from '../components/SlideshowBackground';

interface Course {
  name: string;
  duration: string;
  eligibility: string;
  description: string;
  careerScope: string[];
  avgSalary: string;
  category: string;
  icon: React.ReactNode;
}

const AlliedHealthSciencePage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  // Function to get course link based on course name
  const getCourseLink = (courseName: string) => {
    const courseMap: Record<string, string> = {
      "Optometry": "/courses/optometry",
      "Micro Biology": "/courses/microbiology",
      "Clinical Nutrition": "/courses/clinical-nutrition",
      "Medical Laboratory Technology": "/courses/medical-lab-technology",
      "Radiography and Imaging Technology": "/courses/radiography",
      "Physician Assistant": "/courses/physician-assistant",
      "Operation Theatre & Anaesthesia Technology": "/courses/operation-theatre-technology",
      "Radiotherapy Technology": "/courses/radiotherapy-technology",
      "Accident and Emergency Care Technology": "/courses/emergency-care",
      "Audiology & Speech Language Pathology": "/courses/audiology-speech-pathology",
      "Cardio Pulmonary Perfusion Care Technology": "/courses/cardiovascular-technology",
      "Bachelor of Science in Prosthetics & Orthotics": "/courses/prosthetics-orthotics",
      "Fitness & Lifestyle Modifications": "/courses/fitness-lifestyle",
      "Neuro Electro Physiology": "/courses/neuro-electro-physiology",
      "Medical Sociology": "/courses/medical-sociology",
      "Nuclear Medicine Technology": "/courses/nuclear-medicine-technology",
      "Respiratory Therapy": "/courses/respiratory-therapy",
      "Cardiac Technology": "/courses/cardiac-technology",
      "Dialysis Technology": "/courses/dialysis-technology",
      "Medical Record Science": "/courses/medical-record-science",
      "Critical Care Technology": "/courses/critical-care-technology"
    };
    return courseMap[courseName] || "/allied-health";
  };

  const categories = ['All', 'Diagnostic', 'Therapeutic', 'Support Services', 'Emergency Care'];

  const courses: Course[] = [
    {
      name: "Optometry",
      duration: "4 years (Bachelor) / 2 years (Masters)",
      eligibility: "12th with Physics, Chemistry, Biology/Math (50%+)",
      description: "Study of eye care, vision correction, and eye disease diagnosis and treatment.",
      careerScope: ["Optometrist", "Eye Care Specialist", "Vision Therapist", "Contact Lens Specialist"],
      avgSalary: "₹3-8 LPA",
      category: "Diagnostic",
      icon: <Eye className="h-6 w-6" />
    },
    {
      name: "Micro Biology",
      duration: "3 years (Bachelor) / 2 years (Masters)",
      eligibility: "12th with Physics, Chemistry, Biology (50%+)",
      description: "Study of microorganisms and their effects on humans, animals, and environment.",
      careerScope: ["Microbiologist", "Lab Technician", "Research Scientist", "Quality Control Analyst"],
      avgSalary: "₹2.5-6 LPA",
      category: "Diagnostic",
      icon: <Microscope className="h-6 w-6" />
    },
    {
      name: "Clinical Nutrition",
      duration: "3-4 years",
      eligibility: "12th with Physics, Chemistry, Biology (50%+)",
      description: "Study of nutrition and dietetics for disease prevention and health promotion.",
      careerScope: ["Clinical Nutritionist", "Dietitian", "Food Safety Officer", "Wellness Consultant"],
      avgSalary: "₹3-7 LPA",
      category: "Therapeutic",
      icon: <Activity className="h-6 w-6" />
    },
    {
      name: "Physician Assistant",
      duration: "3 years",
      eligibility: "12th with Physics, Chemistry, Biology (50%+)",
      description: "Healthcare professionals who practice medicine under physician supervision.",
      careerScope: ["Physician Assistant", "Medical Assistant", "Healthcare Coordinator", "Clinical Support"],
      avgSalary: "₹4-10 LPA",
      category: "Support Services",
      icon: <Stethoscope className="h-6 w-6" />
    },
    {
      name: "Radiotherapy Technology",
      duration: "3-4 years",
      eligibility: "12th with Physics, Chemistry, Biology/Math (50%+)",
      description: "Technology and techniques for cancer treatment using radiation therapy.",
      careerScope: ["Radiation Therapist", "Medical Dosimetrist", "Radiation Safety Officer", "Cancer Care Specialist"],
      avgSalary: "₹4-9 LPA",
      category: "Therapeutic",
      icon: <Activity className="h-6 w-6" />
    },
    {
      name: "Accident and Emergency Care Technology",
      duration: "3 years",
      eligibility: "12th with Physics, Chemistry, Biology (50%+)",
      description: "Emergency medical care, trauma management, and critical care technologies.",
      careerScope: ["Emergency Medical Technician", "Paramedic", "Trauma Care Specialist", "Emergency Room Technician"],
      avgSalary: "₹3-8 LPA",
      category: "Emergency Care",
      icon: <Heart className="h-6 w-6" />
    },
    {
      name: "Audiology & Speech Language Pathology",
      duration: "4 years (Bachelor) / 2 years (Masters)",
      eligibility: "12th with Physics, Chemistry, Biology (50%+)",
      description: "Diagnosis and treatment of hearing, speech, and language disorders.",
      careerScope: ["Audiologist", "Speech Therapist", "Hearing Aid Specialist", "Communication Disorder Specialist"],
      avgSalary: "₹3-7 LPA",
      category: "Therapeutic",
      icon: <Brain className="h-6 w-6" />
    },
    {
      name: "Operation Theatre & Anaesthesia Technology",
      duration: "3 years",
      eligibility: "12th with Physics, Chemistry, Biology (50%+)",
      description: "Operating room procedures, anesthesia administration, and surgical support.",
      careerScope: ["OT Technician", "Anesthesia Technician", "Surgical Assistant", "Perioperative Care Specialist"],
      avgSalary: "₹3.5-8 LPA",
      category: "Support Services",
      icon: <Activity className="h-6 w-6" />
    },
    {
      name: "Cardio Pulmonary Perfusion Care Technology",
      duration: "3 years",
      eligibility: "12th with Physics, Chemistry, Biology (50%+)",
      description: "Heart-lung machine operation during cardiac surgeries and procedures.",
      careerScope: ["Perfusionist", "Cardiac Care Technician", "Cardiovascular Specialist", "Heart Surgery Support"],
      avgSalary: "₹4-10 LPA",
      category: "Support Services",
      icon: <Heart className="h-6 w-6" />
    },
    {
      name: "Bachelor of Science in Prosthetics & Orthotics",
      duration: "4 years",
      eligibility: "12th with Physics, Chemistry, Biology/Math (50%+)",
      description: "Design and fitting of artificial limbs and orthotic devices.",
      careerScope: ["Prosthetist", "Orthotist", "Rehabilitation Specialist", "Assistive Technology Expert"],
      avgSalary: "₹3-8 LPA",
      category: "Therapeutic",
      icon: <Activity className="h-6 w-6" />
    },
    {
      name: "Fitness & Lifestyle Modifications",
      duration: "3 years",
      eligibility: "12th with any stream (50%+)",
      description: "Health promotion, fitness training, and lifestyle disease prevention.",
      careerScope: ["Fitness Trainer", "Wellness Coach", "Health Educator", "Corporate Wellness Specialist"],
      avgSalary: "₹2.5-6 LPA",
      category: "Therapeutic",
      icon: <Activity className="h-6 w-6" />
    },
    {
      name: "Radiography and Imaging Technology",
      duration: "3 years",
      eligibility: "12th with Physics, Chemistry, Biology/Math (50%+)",
      description: "Medical imaging techniques including X-ray, CT, MRI, and ultrasound.",
      careerScope: ["Radiographer", "Medical Imaging Technologist", "CT/MRI Technician", "Ultrasound Technician"],
      avgSalary: "₹3-7 LPA",
      category: "Diagnostic",
      icon: <Eye className="h-6 w-6" />
    },
    {
      name: "Neuro Electro Physiology",
      duration: "3 years",
      eligibility: "12th with Physics, Chemistry, Biology (50%+)",
      description: "Brain and nervous system electrical activity monitoring and analysis.",
      careerScope: ["EEG Technician", "Neurophysiology Technologist", "Sleep Study Technician", "Neuro Monitoring Specialist"],
      avgSalary: "₹3.5-8 LPA",
      category: "Diagnostic",
      icon: <Brain className="h-6 w-6" />
    },
    {
      name: "Medical Sociology",
      duration: "3 years",
      eligibility: "12th with any stream (50%+)",
      description: "Study of social factors affecting health, illness, and healthcare systems.",
      careerScope: ["Medical Social Worker", "Healthcare Administrator", "Health Policy Analyst", "Community Health Worker"],
      avgSalary: "₹2.5-6 LPA",
      category: "Support Services",
      icon: <Users className="h-6 w-6" />
    },
    {
      name: "Medical Laboratory Technology",
      duration: "3 years",
      eligibility: "12th with Physics, Chemistry, Biology (50%+)",
      description: "Laboratory testing, sample analysis, and diagnostic procedures.",
      careerScope: ["Lab Technician", "Medical Lab Scientist", "Pathology Assistant", "Quality Control Analyst"],
      avgSalary: "₹2.5-6 LPA",
      category: "Diagnostic",
      icon: <Microscope className="h-6 w-6" />
    },
    {
      name: "Nuclear Medicine Technology",
      duration: "3 years",
      eligibility: "12th with Physics, Chemistry, Biology/Math (50%+)",
      description: "Radioactive materials for medical diagnosis and treatment procedures.",
      careerScope: ["Nuclear Medicine Technologist", "Radiation Safety Officer", "PET/CT Technician", "Nuclear Pharmacy Technician"],
      avgSalary: "₹4-9 LPA",
      category: "Diagnostic",
      icon: <Activity className="h-6 w-6" />
    },
    {
      name: "Respiratory Therapy",
      duration: "3 years",
      eligibility: "12th with Physics, Chemistry, Biology (50%+)",
      description: "Treatment and care of patients with breathing and cardiopulmonary disorders.",
      careerScope: ["Respiratory Therapist", "Pulmonary Function Technician", "Sleep Disorder Specialist", "ICU Support Specialist"],
      avgSalary: "₹3-7 LPA",
      category: "Therapeutic",
      icon: <Heart className="h-6 w-6" />
    },
    {
      name: "Cardiac Technology",
      duration: "3 years",
      eligibility: "12th with Physics, Chemistry, Biology (50%+)",
      description: "Heart diagnostic procedures, ECG, echocardiography, and cardiac monitoring.",
      careerScope: ["Cardiac Technician", "ECG Technician", "Echo Technician", "Cardiac Catheterization Technician"],
      avgSalary: "₹3-8 LPA",
      category: "Diagnostic",
      icon: <Heart className="h-6 w-6" />
    },
    {
      name: "Dialysis Technology",
      duration: "3 years",
      eligibility: "12th with Physics, Chemistry, Biology (50%+)",
      description: "Kidney dialysis procedures, renal replacement therapy, and patient care.",
      careerScope: ["Dialysis Technician", "Nephrology Technician", "Renal Care Specialist", "Dialysis Unit Coordinator"],
      avgSalary: "₹3-7 LPA",
      category: "Therapeutic",
      icon: <Activity className="h-6 w-6" />
    },
    {
      name: "Medical Record Science",
      duration: "3 years",
      eligibility: "12th with any stream (50%+)",
      description: "Medical documentation, health information management, and data analysis.",
      careerScope: ["Medical Records Officer", "Health Information Manager", "Medical Coder", "Healthcare Data Analyst"],
      avgSalary: "₹2.5-6 LPA",
      category: "Support Services",
      icon: <Users className="h-6 w-6" />
    },
    {
      name: "Critical Care Technology",
      duration: "3 years",
      eligibility: "12th with Physics, Chemistry, Biology (50%+)",
      description: "Intensive care unit procedures, life support systems, and critical patient monitoring.",
      careerScope: ["ICU Technician", "Critical Care Specialist", "Ventilator Technician", "Life Support Technician"],
      avgSalary: "₹3.5-8 LPA",
      category: "Emergency Care",
      icon: <Heart className="h-6 w-6" />
    }
  ];

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || course.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });



  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50">
      {/* Hero Section with Slideshow */}
      <section className="relative h-96 overflow-hidden">
        <SlideshowBackground />
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              Allied Health Sciences
            </h1>
            <p className="text-xl md:text-2xl text-green-100 mb-6">
              Comprehensive healthcare support programs for emerging medical professionals
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/premium-services"
                className="inline-block px-8 py-3 bg-green-600 text-white font-bold rounded-lg shadow-xl hover:bg-green-700 transition-all duration-300"
              >
                Get Guidance
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TNEA Admission Status Banner */}
      <div className="w-full bg-gradient-to-r from-red-600 via-orange-500 to-red-600 text-white py-4 overflow-hidden relative z-10 shadow-lg">
        <div className="absolute inset-0 bg-black/20"></div>
        <motion.div 
          className="whitespace-nowrap relative z-20 flex items-center"
          initial={{ x: "100vw" }}
          animate={{ x: "-100%" }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
            repeatType: "loop"
          }}
        >
          <div className="flex items-center space-x-8 text-xl font-bold tracking-wide">
            <span>🚨 IMPORTANT: TNEA 2025 Admissions were closed on 15/09/2025</span>
            <span>•</span>
            <span>📞 For guidance on alternative admissions and next steps, contact our experts</span>
            <span>•</span>
            <span>🎓 We help you find the best alternative pathways</span>
            <span>•</span>
            <span>📱 Contact: +91 77086 77132 (Enquiry) | +91 93610 27132 (Advisor)</span>
            <span>•</span>
            <span>📧 Email: infonammasewa@gmail.com</span>
            <span>•</span>
          </div>
        </motion.div>
      </div>

      {/* Search and Filter Section */}
      <section className="py-8 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Course Overview */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4 text-gray-900">
              About Allied Health Sciences
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Allied Health Sciences encompasses a diverse range of healthcare professions that support, 
              complement, and enhance the services provided by physicians and nurses. These programs prepare 
              students for critical roles in modern healthcare delivery.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {[
              {
                icon: Clock,
                title: "Duration",
                description: "3-4 years programs",
                color: "green"
              },
              {
                icon: Users,
                title: "Career Scope",
                description: "High demand professionals",
                color: "blue"
              },
              {
                icon: Award,
                title: "Eligibility",
                description: "12th with Science stream",
                color: "purple"
              },
              {
                icon: Activity,
                title: "Salary Range",
                description: "₹2.5-10 LPA",
                color: "orange"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-6 rounded-lg bg-gray-50"
              >
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-${item.color}-100 flex items-center justify-center`}>
                  <item.icon className={`h-8 w-8 text-${item.color}-600`} />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4 text-gray-900">
              Available Courses
            </h2>
            <p className="text-lg text-gray-600">
              Showing {filteredCourses.length} courses in {selectedCategory === 'All' ? 'all categories' : selectedCategory}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCourses.map((course, index) => (
              <motion.div
                key={course.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                <Link to={getCourseLink(course.name)} className="block p-6 h-full">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                      {course.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 hover:text-green-600 transition-colors">{course.name}</h3>
                      <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                        course.category === 'Diagnostic' ? 'bg-blue-100 text-blue-800' :
                        course.category === 'Therapeutic' ? 'bg-green-100 text-green-800' :
                        course.category === 'Emergency Care' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {course.category}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-4 text-sm">{course.description}</p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Duration:</span>
                      <span className="font-medium">{course.duration}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Avg. Salary:</span>
                      <span className="font-medium text-green-600">{course.avgSalary}</span>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Career Opportunities:</h4>
                    <div className="flex flex-wrap gap-1">
                      {course.careerScope.slice(0, 3).map((career, i) => (
                        <span key={i} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                          {career}
                        </span>
                      ))}
                      {course.careerScope.length > 3 && (
                        <span className="text-xs text-gray-500">+{course.careerScope.length - 3} more</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mb-2">Eligibility:</p>
                    <p className="text-sm font-medium text-gray-700">{course.eligibility}</p>
                  </div>
                  
                  <div className="mt-4 flex items-center text-green-600 text-sm font-medium">
                    <span>View Course Details</span>
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Start Your Allied Health Career?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Get expert guidance on course selection, college admissions, and career planning in Allied Health Sciences.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/premium-services"
                className="inline-block px-8 py-4 bg-white text-green-600 font-bold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                Book Free Consultation
              </Link>
              <Link
                to="/colleges"
                className="inline-block px-8 py-4 border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-green-600 transition-all duration-300"
              >
                Explore Scholarships
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AlliedHealthSciencePage;